import api from './client';

// Mock Data Audit API
export const mockDataAuditAPI = {
  audit: () => api.get('/admin/diagnostics/mock-data-audit'),
  purge: (preview = true, body = null) => api.post(`/admin/diagnostics/mock-data-audit/purge?preview=${preview}`, body),
  purgeManifests: () => api.get('/admin/diagnostics/mock-data-audit/purge-manifests'),
  purgeManifest: (id) => api.get(`/admin/diagnostics/mock-data-audit/purge-manifests/${id}`),
  correctionsPreview: () => api.get('/admin/diagnostics/mock-data-audit/corrections-preview', { timeout: 30000 }),
  correctionsApply: () => api.post('/admin/diagnostics/mock-data-audit/corrections-apply', null, { timeout: 30000 }),
  bridgeSandboxTagPreview: () => api.get('/admin/diagnostics/mock-data-audit/bridge-sandbox-tag-preview', { timeout: 30000 }),
  bridgeSandboxTagApply: () => api.post('/admin/diagnostics/mock-data-audit/bridge-sandbox-tag-apply', null, { timeout: 30000 }),
};

// Management Statement Duplicate Cleanup: preview/apply soft-exclusion of
// duplicate management_statements docs created by the AI-text-drift dedup
// bug (services/statement_duplicate_cleanup.py). Never a hard delete.
export const statementDuplicateCleanupAPI = {
  preview: () => api.get('/admin/statement-duplicate-cleanup/preview', { timeout: 30000 }),
  apply: () => api.post('/admin/statement-duplicate-cleanup/apply', null, { timeout: 30000 }),
};

// Statement Duplicate Census: read-only, groups the same non-excluded
// management_statements docs under four candidate identity keys so the
// real prod duplicate shape is visible even when the cleanup card above
// reports zero groups. GET only, no apply.
export const statementDuplicateCensusAPI = {
  run: () => api.get('/admin/statement-duplicate-census', { timeout: 30000 }),
  // Orphaned Links: the Management Statements trash icon hard-deletes
  // (no soft-exclude), leaving statement_payout_matches docs pointing at
  // dead statement_id values. Finds them and flags which have a surviving
  // same-(landlord_entity_id, statement_month) twin they could be
  // re-pointed to -- the restore-candidate population, not a restore.
  // GET only, no apply.
  runOrphanedLinks: () => api.get('/admin/statement-duplicate-census/orphaned-links', { timeout: 30000 }),
};

// Statement Identity Migration: drops the legacy uniq_mgmt_stmt_statement_id
// unique index on db.management_statements once the new (property_ref-or-
// normalized-name, statement_month) indexes are live and a LIVE re-check
// confirms zero duplicate groups remain under that key. Preflight is
// read-only; Apply re-checks live and refuses (409) if anything is dirty.
// Idempotent -- a second Apply call once already applied is a no-op.
export const statementIdentityMigrationAPI = {
  preflight: () => api.get('/admin/statement-identity-migration/preflight', { timeout: 30000 }),
  apply: () => api.post('/admin/statement-identity-migration/apply', null, { timeout: 30000 }),
};

// Recent API Errors
export const recentErrorsAPI = {
  get: () => api.get('/admin/diagnostics/recent-api-errors'),
};

// Admin Diagnostics API
export const diagnosticsAPI = {
  buildInfo: () => api.get('/admin/diagnostics/build-info'),
  // Management Statement Backfill (from Jan 2026): finds CWC-style
  // statement mail the label-gated scan missed, and invoice docs
  // mis-ingested before the sender+keyword routing-rule fix. Budget-boxed
  // chunked chains (same engine as gmailAmountReextraction* elsewhere in
  // this object).
  statementBackfillPreviewChainStart: () =>
    api.post('/admin/diagnostics/statement-backfill/preview/chain-start'),
  statementBackfillPreviewChainStatus: () =>
    api.get('/admin/diagnostics/statement-backfill/preview/chain-status'),
  statementBackfillPreviewChainStop: (chainId) =>
    api.post('/admin/diagnostics/statement-backfill/preview/chain-stop', { chain_id: chainId }),
  statementBackfillApplyChainStart: () =>
    api.post('/admin/diagnostics/statement-backfill/apply/chain-start'),
  statementBackfillApplyChainStatus: () =>
    api.get('/admin/diagnostics/statement-backfill/apply/chain-status'),
  statementBackfillApplyChainStop: (chainId) =>
    api.post('/admin/diagnostics/statement-backfill/apply/chain-stop', { chain_id: chainId }),
  statementBackfillJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/statement-backfill/job-status/${jobId}`),
  statementBackfillJobLatest: (operation) =>
    api.get(`/admin/diagnostics/statement-backfill/job-latest/${operation}`),
  // Manual Gmail supplier-invoice backfill: operator-chosen date range
  // (+ optional sender) run through the same ingestion pipeline the
  // recurring scheduled scan uses. Budget-boxed chunked chain, single
  // operation (no preview/apply split -- see services/gmail_invoice_
  // backfill.py's docstring).
  gmailInvoiceBackfillStart: (dateFrom, dateTo, sender) =>
    api.post('/admin/diagnostics/gmail-invoice-backfill/start', {
      date_from: dateFrom, date_to: dateTo, sender: sender || null,
    }),
  gmailInvoiceBackfillStatus: () => api.get('/admin/diagnostics/gmail-invoice-backfill/status'),
  gmailInvoiceBackfillStop: (chainId) =>
    api.post('/admin/diagnostics/gmail-invoice-backfill/stop', { chain_id: chainId }),
  gmailInvoiceBackfillJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/gmail-invoice-backfill/job-status/${jobId}`),
  gmailInvoiceBackfillJobLatest: () => api.get('/admin/diagnostics/gmail-invoice-backfill/job-latest'),
  // Mailbox Ingestion Coverage: account health + live selection query +
  // per-sender census (chunked chain) + a bounded single-sender probe +
  // the operator-editable scope config (add senders, preview, apply).
  mailboxIngestionCoverageSummary: () => api.get('/admin/diagnostics/mailbox-ingestion-coverage', { timeout: 30000 }),
  mailboxIngestionCoverageProbe: (sender, windowDays) =>
    api.get('/admin/diagnostics/mailbox-ingestion-coverage/probe', {
      params: { sender, window_days: windowDays || 90 }, timeout: 60000,
    }),
  mailboxIngestionCoverageCensusStart: (windowDays) =>
    api.post('/admin/diagnostics/mailbox-ingestion-coverage/census/start', { window_days: windowDays || 90 }),
  mailboxIngestionCoverageCensusStatus: () => api.get('/admin/diagnostics/mailbox-ingestion-coverage/census/status'),
  mailboxIngestionCoverageCensusStop: (chainId) =>
    api.post('/admin/diagnostics/mailbox-ingestion-coverage/census/stop', { chain_id: chainId }),
  mailboxIngestionCoverageCensusJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/mailbox-ingestion-coverage/census/job-status/${jobId}`),
  mailboxIngestionCoverageCensusJobLatest: () =>
    api.get('/admin/diagnostics/mailbox-ingestion-coverage/census/job-latest'),
  mailboxIngestionScopeConfigGet: () => api.get('/admin/diagnostics/mailbox-ingestion-coverage/scope-config'),
  mailboxIngestionScopeConfigPreview: (senders, windowDays) =>
    api.post('/admin/diagnostics/mailbox-ingestion-coverage/scope-config/preview', {
      senders, window_days: windowDays || 90,
    }, { timeout: 60000 }),
  mailboxIngestionScopeConfigApply: (senders, windowDays) =>
    api.post('/admin/diagnostics/mailbox-ingestion-coverage/scope-config/apply', {
      senders, window_days: windowDays || 90,
    }),
  mailboxIngestionScopeConfigRemove: (sender) =>
    api.post('/admin/diagnostics/mailbox-ingestion-coverage/scope-config/remove', { sender }),
  bankBalanceBreakdown: () => api.get('/admin/diagnostics/bank-balance-breakdown', { timeout: 60000 }),
  bankSyncFreshness: () => api.get('/admin/diagnostics/bank-sync-freshness', { timeout: 60000 }),
  duplicateInvoiceCandidates: () => api.get('/admin/diagnostics/duplicate-invoice-candidates', { timeout: 60000 }),
  invoiceFileCoverage: () => api.get('/admin/diagnostics/invoice-file-coverage', { timeout: 60000 }),
  runtimeConfig: () => api.get('/admin/diagnostics/runtime-config'),
  monitorSummary: () => api.get('/admin/diagnostics/monitor-summary'),
  receiptIngestSummary: () => api.get('/admin/diagnostics/receipt-ingest-summary'),
  occupancy: (params) => api.get('/admin/diagnostics/occupancy', { params }),
  occupancyAudit: (params) => api.get('/admin/diagnostics/occupancy-audit', { params }),
  bookingTrace: (params) => api.get('/admin/diagnostics/booking-trace', { params }),
  propertyBookingsDump: (params) => api.get('/admin/diagnostics/property-bookings-dump', { params }),
  syncAudit: () => api.get('/admin/uplisting/sync-audit'),
  backfillProperty: (propertyId) => api.post(`/admin/uplisting/backfill-property/${propertyId}`),
  propertyCurrencyAudit: () => api.get('/admin/portfolio/property-currency-audit'),
  webhookLogTail: (params) => api.get('/admin/uplisting/webhook-log-tail', { params }),
  recentReservations: (params) => api.get('/admin/uplisting/recent-reservations', { params }),
  routeIntrospection: () => api.get('/admin/uplisting/route-introspection'),
  propertySyncState: (params) => api.get('/admin/uplisting/property-sync-state', { params }),
  lastSyncPerProperty: () => api.get('/admin/uplisting/last-sync-per-property'),
  syncLogTail: (params) => api.get('/admin/uplisting/sync-log-tail', { params }),
  propertySyncProbe: (propertyId) => api.get(`/admin/uplisting/property-sync-probe/${propertyId}`),
  bookingProbe: (params) => api.get('/admin/diagnostics/uplisting-booking-probe', { params, timeout: 60000 }),
  bookingCensus: (params) => api.get('/admin/diagnostics/uplisting-booking-census', { params, timeout: 90000 }),
  // BR OS supplier-data diagnostic
  brosSupplierDataDiagnostic: (period = 'ytd') => api.get('/admin/bros/supplier-data-diagnostic', { params: { period } }),
  // BR OS supplier cost lineage (expected vs actual, side-by-side)
  brosSupplierCostLineage: (period = 'ytd') => api.get('/admin/bros/supplier-cost-lineage', { params: { period }, timeout: 60000 }),
  // BR OS data quality diagnostics
  brosVatCheck: () => api.get('/admin/bros/vat-check'),
  brosMarginConsistency: () => api.get('/admin/bros/margin-consistency'),
  brosStatusDistribution: () => api.get('/admin/bros/status-distribution'),
  brosCurrencyDistribution: () => api.get('/admin/bros/currency-distribution'),
  brosClientNames: () => api.get('/admin/bros/client-names'),
  // BR OS sync health
  brosSyncState: () => api.get('/admin/bros/sync-state'),
  brosProbe: () => api.get('/admin/bros/probe'),
  brosInvoiceProbe: (brosId) => api.get(`/admin/bros/invoice-probe/${brosId}`),
  brosBookingsHierarchyCheck: () => api.get('/admin/bros/bookings-hierarchy-check', { timeout: 120000 }),
  cashflowAgingBucketContents: (params) => api.get('/admin/diagnostics/cashflow-aging-bucket-contents', { params }),
  cashflowMonthlyInvoicingContents: (params) => api.get('/admin/diagnostics/cashflow-monthly-invoicing-contents', { params }),
  billingVsSalesContents: (params) => api.get('/admin/diagnostics/billing-vs-sales-contents', { params }),
  billingVsSalesDivergenceCheck: (params) => api.get('/admin/diagnostics/billing-vs-sales-divergence-check', { params }),
  // BR OS invoice status check
  brosInvoiceStatusCheck: (params) => api.get('/admin/diagnostics/br-os-invoice-status-check', { params }),
  // Xero connections management (list + revoke)
  xeroConnections: () => api.get('/admin/diagnostics/xero-connections'),
  xeroConnectionRevoke: (connectionId) =>
    api.post('/admin/diagnostics/xero-connections/revoke', { connection_id: connectionId }),
  // Xero Connections Census: read-only duplicate/unknown-tenant/cap report,
  // plus a dry_run-gated single-connection disconnect (Preview then confirm).
  xeroConnectionsCensus: () => api.get('/admin/diagnostics/xero-connections-census'),
  xeroConnectionsCensusDisconnectPreview: (connectionId) =>
    api.post('/admin/diagnostics/xero-connections-census/disconnect', {
      connection_id: connectionId,
      dry_run: true,
    }),
  xeroConnectionsCensusDisconnectConfirm: (connectionId) =>
    api.post('/admin/diagnostics/xero-connections-census/disconnect', {
      connection_id: connectionId,
      dry_run: false,
    }),
  // BR OS invoice-Xero linkage diagnostic
  brosInvoiceXeroLinkage: () => api.get('/admin/bros/invoice-xero-linkage-diagnostic'),
  // Xero invoice link backfill (preview is DB-only, apply dispatches background job)
  xeroLinkBackfillPreview: () => api.get('/admin/bros/xero-link-backfill/preview'),
  xeroLinkBackfillApply: () => api.post('/admin/bros/xero-link-backfill/apply'),
  xeroLinkBackfillStatus: () => api.get('/admin/bros/xero-link-backfill/status'),
  xeroLinkBackfillReset: () => api.post('/admin/bros/xero-link-backfill/reset'),
  // Read-only diagnostic for why xero-link-backfill Preview stays at 0 linked
  // after the c114ddb4/8e3494d6 sync self-heal + propagation fixes.
  xeroLinkBlockerDiagnostic: () => api.get('/admin/bros/xero-link-blocker-diagnostic'),
  // Xero would-quarantine diagnostic (read-only scan of synced data)
  xeroWouldQuarantine: () => api.get('/admin/diagnostics/xero-would-quarantine'),
  // Invoice series alignment (DB-only, zero Xero calls)
  invoiceSeriesAlignment: () => api.get('/admin/diagnostics/invoice-series-alignment', { timeout: 60000 }),
  // Invoice content-correlation diagnostic (DB-only, zero Xero calls)
  invoiceContentCorrelation: () => api.get('/admin/diagnostics/invoice-content-correlation', { timeout: 60000 }),
  // Xero PDF original for a bros invoice (streamed from Xero)
  fetchBrosInvoiceXeroPdf: (invoiceId) => api.get(`/admin/invoices/${invoiceId}/pdf`, { responseType: 'blob', timeout: 30000 }),
  // Invoice PDF coverage diagnostic
  invoicePdfCoverage: () => api.get('/admin/diagnostics/invoice-pdf-coverage', { timeout: 120000 }),
  // Invoice PDF live resolver probe (sample of 10, never full set). Preview
  // writes nothing; Apply persists resolved links (gate-passing only).
  invoicePdfLiveProbe: () => api.get('/admin/diagnostics/invoice-pdf-live-probe', { timeout: 120000 }),
  invoicePdfLiveProbeApply: () => api.post('/admin/diagnostics/invoice-pdf-live-probe/apply', {}, { timeout: 120000 }),
  // BR OS PDF reachability diagnostic (live probe per sampled invoice)
  brosPdfReachability: () => api.get('/admin/diagnostics/bros-pdf-reachability', { timeout: 120000 }),
  // Client invoice reconcile diagnostic (not-found fork analysis)
  clientInvoiceReconcile: (params) => api.get('/admin/diagnostics/client-invoice-reconcile', { params, timeout: 120000 }),
  // Issued vs Invoiced waterfall (gap decomposition diagnostic)
  issuedVsInvoicedWaterfall: (params) => api.get('/admin/diagnostics/issued-vs-invoiced-waterfall', { params, timeout: 60000 }),
  // BR OS removed-from-source preview
  brosPreviewRemovedFromSource: () => api.get('/admin/bros/preview-removed-from-source', { timeout: 120000 }),
  // Pennylane diagnostics
  pennylaneSeedEntity: (body) => api.post('/admin/pennylane/seed-entity', body),
  pennylaneConnectionCheck: (entityId) => api.get('/admin/diagnostics/pennylane-connection-check', { params: { entity_id: entityId } }),
  pennylaneEntities: () => api.get('/admin/diagnostics/pennylane-entities'),
  uplistingStampingInvariants: () => api.get('/admin/diagnostics/uplisting-stamping-invariants', { timeout: 60000 }),
  pennylaneConnectionTest: (entityId) => api.get('/admin/diagnostics/pennylane-connection-test', { params: { entity_id: entityId } }),
  pennylaneBankFeedDiscovery: (entityId, accountId) => api.get('/admin/diagnostics/pennylane-bank-feed-discovery', { params: { entity_id: entityId, account_id: accountId || undefined }, timeout: 60000 }),
  hostawayDiscovery: () => api.get('/admin/diagnostics/hostaway-discovery', { timeout: 60000 }),
  bookingComFeeAudit: () => api.get('/admin/diagnostics/booking-com-fee-audit', { timeout: 60000 }),
  hostawayBackfillChannelCommission: ({ dryRun } = {}) =>
    api.post('/admin/diagnostics/hostaway-backfill-channel-commission', {
      dry_run: dryRun,
    }, { timeout: 300000 }),
  hostawayDedupPairProbe: (params) => api.get('/admin/diagnostics/hostaway-dedup-pair-probe', { params, timeout: 60000 }),
  migrationBookingProbePropertyOptions: () => api.get('/admin/diagnostics/migration-booking-probe/property-options', { timeout: 60000 }),
  migrationBookingProbe: (params) => api.get('/admin/diagnostics/migration-booking-probe', { params, timeout: 60000 }),
  migrationBookingProbeCurrencyBackfillPreview: () =>
    api.get('/admin/diagnostics/migration-booking-probe/currency-backfill-preview', { timeout: 60000 }),
  migrationBookingProbeCurrencyBackfillApply: () =>
    api.post('/admin/diagnostics/migration-booking-probe/currency-backfill-apply', null, { timeout: 120000 }),
  statementReconciliationInspect: (params) => api.get('/admin/statement-reconciliation-inspect', { params, timeout: 60000 }),
  statementReconciliationInspectPropertyOptions: () => api.get('/admin/statement-reconciliation-inspect/property-options', { timeout: 60000 }),
  statementReconciliationBulkRecomputePreview: () => api.get('/admin/statement-reconciliation-bulk-recompute-preview', { timeout: 120000 }),
  statementReconciliationBulkRecomputeApply: () => api.post('/admin/statement-reconciliation-bulk-recompute-apply', null, { timeout: 120000 }),
  smoobuReservationTrace: (params) => api.get('/admin/diagnostics/smoobu-reservation-trace', { params, timeout: 60000 }),
  pennylaneWriteProof: (entityId, confirm) => api.post('/admin/diagnostics/pennylane-write-proof', { entity_id: entityId, confirm }),
  pennylaneInvoiceState: (params) => api.get('/admin/diagnostics/pennylane-invoice-state', { params, timeout: 60000 }),
  // Counterparty payload-shape probe (CLAUDE.md Prompt 8 Part 1) -- run against prod from the browser
  pennylaneCounterpartyProbe: (entityId) => api.get('/admin/diagnostics/pennylane-counterparty-probe', { params: { entity_id: entityId }, timeout: 60000 }),
  finalizeSequence: (params) => api.get('/admin/diagnostics/finalize-sequence', { params, timeout: 120000 }),
  invoiceGenerationStatus: () => api.get('/admin/diagnostics/invoice-generation-status', { timeout: 30000 }),
  invoiceGenerationRun: (entityId) => api.post(`/admin/diagnostics/invoice-generation-run?entity_id=${entityId}`, null, { timeout: 120000 }),
  consolidatePersonalPreview: () => api.get('/admin/diagnostics/consolidate-personal-preview', { timeout: 30000 }),
  consolidatePersonalApply: () => api.post('/admin/diagnostics/consolidate-personal-apply', null, { timeout: 30000 }),
  portfolioChartDivergence: (params) => api.get('/admin/diagnostics/portfolio-chart-divergence', { params }),
  // Smoobu diagnostics
  smoobuCancelledAudit: () => api.get('/admin/diagnostics/smoobu-cancelled-audit'),
  smoobuRawReservation: (reservationId) =>
    api.get('/admin/diagnostics/smoobu-raw-reservation', { params: { reservation_id: reservationId } }),
  smoobuBackfillCancellationStatus: ({ dryRun, cursor = null, batchSize = 20 } = {}) =>
    api.post('/admin/diagnostics/smoobu-backfill-cancellation-status', {
      dry_run: dryRun, cursor, batch_size: batchSize,
    }, { timeout: 30000 }),
  cancelledCohortRateCheck: (params) =>
    api.get('/admin/diagnostics/cancelled-cohort-rate-check', { params, timeout: 120000 }),
  nightlyRateCancelledExclusion: (params) =>
    api.get('/admin/diagnostics/nightly-rate-cancelled-exclusion', { params, timeout: 120000 }),
  // Preview is a local Mongo scan (fast, synchronous). Apply used to run a
  // full paginated Smoobu API resync in-request (the 120s client timeout
  // above was itself evidence of that risk) -- it now starts a JobStore
  // background job and the panel polls job-status/job-latest instead.
  smoobuBackfillChannelCommissionPreview: () =>
    api.post('/admin/diagnostics/smoobu-backfill-channel-commission'),
  smoobuBackfillChannelCommissionApplyStart: () =>
    api.post('/admin/diagnostics/smoobu-backfill-channel-commission/apply/start'),
  smoobuBackfillCurrencyPreview: () =>
    api.post('/admin/diagnostics/smoobu-backfill-currency'),
  smoobuBackfillCurrencyApplyStart: () =>
    api.post('/admin/diagnostics/smoobu-backfill-currency/apply/start'),
  smoobuBackfillJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/smoobu-backfill/job-status/${jobId}`),
  smoobuBackfillJobLatest: (operation) =>
    api.get(`/admin/diagnostics/smoobu-backfill/job-latest/${operation}`),
  smoobuTypeVocabulary: ({ cursor = null, batchSize = 6 } = {}) =>
    api.post('/admin/diagnostics/smoobu-type-vocabulary', {
      cursor, batch_size: batchSize,
    }, { timeout: 30000 }),
  smoobuProperties: () => api.get('/admin/diagnostics/smoobu-properties'),
  reingestQuarantinedWebhooks: () =>
    api.post('/admin/uplisting/reingest-quarantined-webhooks', null, { timeout: 120000 }),
  quarantinedWebhookCount: () =>
    api.get('/admin/uplisting/quarantined-webhook-count'),
  uplistingDuplicatesAndCancellations: () =>
    api.get('/admin/diagnostics/uplisting-duplicates-and-cancellations', { timeout: 60000 }),
  uplistingStatusVocabulary: () =>
    api.get('/admin/diagnostics/uplisting-status-vocabulary', { timeout: 60000 }),
  uplistingBackfillCancellationStatus: ({ dryRun, cursor = null, batchSize = 20 } = {}) =>
    api.post('/admin/uplisting/backfill-cancellation-status', {
      dry_run: dryRun, cursor, batch_size: batchSize,
    }, { timeout: 60000 }),
  uplistingCancellationFeeExposure: () =>
    api.get('/admin/diagnostics/uplisting-cancellation-fee-exposure', { timeout: 120000 }),
  uplistingWebhookHealth: () =>
    api.get('/admin/diagnostics/uplisting-webhook-health', { timeout: 30000 }),
  reregisterUplistingHooks: () =>
    api.post('/admin/diagnostics/uplisting-reregister-hooks', null, { timeout: 30000 }),
  managedPropertyAttribution: () =>
    api.get('/admin/diagnostics/managed-property-attribution', { timeout: 120000 }),
  entityMismatchAudit: () =>
    api.get('/admin/diagnostics/entity-mismatch-audit', { timeout: 120000 }),
  entityIdCorrectionPreview: () =>
    api.get('/admin/diagnostics/entity-id-correction-preview', { timeout: 120000 }),
  entityIdCorrectionApply: () =>
    api.post('/admin/diagnostics/entity-id-correction-apply', null, { timeout: 120000 }),
  // FR invoice preview (read-only, no Pennylane calls)
  pennylaneInvoicePreview: (params) =>
    api.get('/admin/diagnostics/pennylane-invoice-preview', { params, timeout: 120000 }),
  // Stale-confirmed detector
  staleConfirmedBookings: () =>
    api.get('/admin/diagnostics/stale-confirmed-bookings', { timeout: 120000 }),
  // Manual cancellation override
  manualCancelPreview: (uplistingId, cancellationFee = 0) =>
    api.get(`/admin/uplisting/manual-cancel-preview/${uplistingId}`, {
      params: { cancellation_fee: cancellationFee },
    }),
  manualCancel: (uplistingId, { cancellationFee = 0, reason, confirm = true } = {}) =>
    api.post(`/admin/uplisting/manual-cancel/${uplistingId}`, {
      cancellation_fee: cancellationFee,
      reason,
      confirm,
    }),
  // Guest name backfill
  guestNameBackfillPreview: () =>
    api.get('/admin/diagnostics/guest-name-backfill-preview', { timeout: 120000 }),
  guestNameBackfillApply: () =>
    api.post('/admin/diagnostics/guest-name-backfill-apply', null, { timeout: 120000 }),
  // Subscription beneficiary-name backfill (Deploy B Fix 1)
  subscriptionBeneficiaryBackfillPreview: () =>
    api.get('/admin/diagnostics/subscription-beneficiary-backfill', { timeout: 120000 }),
  subscriptionBeneficiaryBackfillApply: () =>
    api.post('/admin/diagnostics/subscription-beneficiary-backfill', null, { timeout: 120000 }),
  // Connected-account dedupe (ingestion deep-dive Step 3)
  accountDedupePreview: () =>
    api.get('/admin/diagnostics/account-dedupe', { timeout: 120000 }),
  accountDedupeApply: () =>
    api.post('/admin/diagnostics/account-dedupe', null, { timeout: 120000 }),
  // Cancelled management_fee backfill
  backfillCancelledManagementFee: ({ dryRun } = {}) =>
    api.post('/admin/backfill-cancelled-management-fee', {
      dry_run: dryRun,
    }, { timeout: 60000 }),
  // management_type restamp backfill (4bb8767e backlog)
  managementTypeBackfillCensus: () =>
    api.get('/admin/diagnostics/management-type-backfill-census', { timeout: 60000 }),
  managementTypeBackfillPreview: () =>
    api.get('/admin/diagnostics/management-type-backfill-preview', { timeout: 60000 }),
  managementTypeBackfillApply: () =>
    api.post('/admin/diagnostics/management-type-backfill-apply', null, { timeout: 60000 }),
  // management_type contradiction census (property says not-managed, statements say it is)
  managementTypeContradictionCensus: () =>
    api.get('/admin/diagnostics/management-type-contradiction-census', { timeout: 60000 }),
  setPropertyManagementType: ({ platform, propertyId, managementType, managementFeeRate }) =>
    api.patch('/admin/diagnostics/property-management-type', {
      platform,
      property_id: propertyId,
      management_type: managementType,
      management_fee_rate: managementFeeRate ?? null,
    }),
  // Entity-blindness tally (entities neither the restamp nor the
  // contradiction census above can see -- the general form of the ECI gap)
  entityBlindnessTally: () =>
    api.get('/admin/diagnostics/entity-blindness-tally', { timeout: 60000 }),
  // Property reclassification (Fan's ruling: RULED_NEVER_SELF_OPERATED
  // entities -- eci, bi_ltd, bi_uk -- never self-operate a property)
  propertyReclassificationRuledEntities: () =>
    api.get('/admin/diagnostics/property-reclassification-ruled-entities', { timeout: 60000 }),
  propertyReclassificationPreview: (entityId) =>
    api.get('/admin/diagnostics/property-reclassification-preview', {
      params: { entity_id: entityId },
      timeout: 60000,
    }),
  propertyReclassificationApply: (entityId) =>
    api.post('/admin/diagnostics/property-reclassification-apply', { entity_id: entityId }, { timeout: 60000 }),
  // BR OS invoice matching_status/payment_status clobber-damage restore
  // (services/bros.py hourly resync clobber, fixed c2a82135)
  brosClobberRestorePreview: () =>
    api.get('/admin/diagnostics/bros-clobber-restore-preview', { timeout: 60000 }),
  brosClobberRestoreApply: () =>
    api.post('/admin/diagnostics/bros-clobber-restore-apply', null, { timeout: 60000 }),
  // Managed property invoice cleanup (credit notes / delete drafts)
  managedInvoiceCleanupPreview: () =>
    api.get('/admin/diagnostics/managed-invoice-cleanup-preview', { timeout: 120000 }),
  managedInvoiceCleanupApply: ({ limit } = {}) =>
    api.post(
      '/admin/diagnostics/managed-invoice-cleanup-apply',
      null,
      { timeout: 120000, params: limit != null ? { limit } : undefined },
    ),
  // Credit-note payload diff (read-only diagnostic)
  creditNoteDiff: (bookingId) =>
    api.get('/admin/managed-invoice-cleanup/credit-note-diff', {
      params: { booking_id: bookingId },
      timeout: 120000,
    }),
  // Credit-note VAT audit (post-hoc per-rate verification)
  creditNoteVatAudit: () =>
    api.get('/admin/diagnostics/credit-note-vat-audit', { timeout: 120000 }),
  // Statement scan window diagnostic
  statementScanWindow: () =>
    api.get('/admin/diagnostics/statement-scan-window'),
  // CWC match preview
  cwcMatchPreview: () =>
    api.get('/admin/diagnostics/cwc-match-preview', { timeout: 60000 }),
  // Pennylane reconciliation preview
  pennylaneReconciliationPreview: (params) =>
    api.get('/admin/diagnostics/pennylane-reconciliation-preview', { params, timeout: 120000 }),
  // Pennylane recon apply/unapply (AFPV-only write endpoints)
  pennylaneReconApplyMatch: (payload) =>
    api.post('/admin/diagnostics/pennylane-recon-apply-match', payload, { timeout: 30000 }),
  pennylaneReconUnapplyMatch: (payload) =>
    api.post('/admin/diagnostics/pennylane-recon-unapply-match', payload, { timeout: 30000 }),
  // Pennylane amount+date match diagnostic (read-only)
  pennylaneReconAmountDateDiagnostic: (params) =>
    api.get('/admin/diagnostics/pennylane-recon-amount-date-diagnostic', { params, timeout: 120000 }),
  // Tier 0 diagnostics front door
  pennylaneSyncRunNow: () =>
    api.post('/admin/pennylane/sync-run-now', null, { timeout: 120000 }),
  pennylaneReconciliationRunNow: () =>
    api.post('/admin/pennylane/reconciliation-run-now', null, { timeout: 120000 }),
  tier0Inspect: (bookingId) =>
    api.get(`/admin/pennylane/tier0-inspect/${bookingId}`, { timeout: 30000 }),
  tier0Populations: () =>
    api.get('/admin/pennylane/tier0-populations', { timeout: 60000 }),
  // Supplier-payment match preview (Wise)
  supplierPaymentMatchPreview: () =>
    api.get('/admin/diagnostics/supplier-payment-match-preview', { timeout: 120000 }),
  // Statement list mismatch diagnostic
  statementListMismatch: (params) =>
    api.get('/admin/diagnostics/statement-list-mismatch', { params, timeout: 60000 }),
  // Cleaning verification feasibility (UK)
  cleaningVerificationFeasibility: (params) =>
    api.get('/admin/diagnostics/cleaning-verification-feasibility', { params, timeout: 60000 }),
  // Intake health (Gmail + CoS credential and freshness check)
  intakeHealth: () => api.get('/admin/diagnostics/intake-health', { timeout: 30000 }),
  // FX rate freshness (per-pair newest rate date)
  fxFreshness: () => api.get('/admin/diagnostics/fx-freshness', { timeout: 10000 }),
  // FX blast radius: documents that captured a fallback rate during the stale window
  fxBlastRadius: () => api.get('/admin/diagnostics/fx-blast-radius', { timeout: 30000 }),
  // Backfill external_reservation_id (Booking.com)
  backfillExternalResIdPreview: (params) =>
    api.get('/admin/diagnostics/backfill-external-res-id-preview', { params, timeout: 120000 }),
  backfillExternalResIdApply: (params) =>
    api.post('/admin/diagnostics/backfill-external-res-id-apply', null, { params, timeout: 120000 }),
  // No-supplier property resolver (diagnose + backfill)
  noSupplierPropertyResolver: () =>
    api.get('/admin/diagnostics/no-supplier-property-resolver', { timeout: 120000 }),
  noSupplierBackfillPreview: () =>
    api.get('/admin/diagnostics/no-supplier-backfill-preview', { timeout: 120000 }),
  noSupplierBackfillApply: () =>
    api.post('/admin/diagnostics/no-supplier-backfill-apply', null, { timeout: 120000 }),
  // Property supplier probe (BR OS field-path check)
  propertySupplierProbe: () =>
    api.get('/admin/bros/property-supplier-probe', { timeout: 120000 }),
  // Cleaning verification (UK, per-clean against Smoobu check-outs)
  cleaningVerification: (params) =>
    api.get('/admin/diagnostics/cleaning-verification', { params, timeout: 60000 }),
  // Cleaning verification year read (all persisted months, no recompute)
  cleaningVerificationYear: (params) =>
    api.get('/admin/diagnostics/cleaning-verification-year', { params, timeout: 60000 }),
  // Cleaning verification coverage gap set (read-only)
  cleaningVerificationCoverage: () =>
    api.get('/admin/diagnostics/cleaning-verification-coverage', { timeout: 60000 }),
  // Cleaning verification reconcile (recompute all gaps)
  cleaningVerificationReconcile: () =>
    api.post('/admin/diagnostics/cleaning-verification-reconcile', null, { timeout: 120000 }),
  // Commission verification coverage + reconcile
  commissionVerificationYear: (params) =>
    api.get('/admin/diagnostics/commission-verification-year', { params, timeout: 60000 }),
  commissionVerificationCoverage: () =>
    api.get('/admin/diagnostics/commission-verification-coverage', { timeout: 60000 }),
  commissionVerificationReconcile: () =>
    api.post('/admin/diagnostics/commission-verification-reconcile', null, { timeout: 120000 }),
  commissionVerificationRecompute: (params) =>
    api.post('/admin/commission-verification/recompute', null, { params, timeout: 60000 }),
  // Maintenance verification (maintenance expenses from statements)
  maintenanceVerificationYear: (params) =>
    api.get('/admin/diagnostics/maintenance-verification-year', { params, timeout: 60000 }),
  maintenanceVerificationCoverage: () =>
    api.get('/admin/diagnostics/maintenance-verification-coverage', { timeout: 60000 }),
  maintenanceVerificationReconcile: () =>
    api.post('/admin/diagnostics/maintenance-verification-reconcile', null, { timeout: 120000 }),
  // Expense coverage diagnostic (cleaning / maintenance / unclassified split)
  expenseCoverage: (params) =>
    api.get('/admin/diagnostics/expense-coverage', { params, timeout: 60000 }),
  // No-supplier batch audit (classify all 60 no-supplier bookings via API)
  noSupplierAudit: () =>
    api.get('/admin/bros/no-supplier-audit', { timeout: 180000 }),
  // Statement landlord backfill (preview + apply)
  statementLandlordBackfillPreview: () =>
    api.get('/admin/diagnostics/statement-landlord-backfill-preview', { timeout: 120000 }),
  statementLandlordBackfillApply: () =>
    api.post('/admin/diagnostics/statement-landlord-backfill-apply', null, { timeout: 120000 }),
  // Statement landlord assign (unmapped landlords + assign alias)
  unmappedStatementLandlords: () =>
    api.get('/admin/statements/unmapped-landlords', { timeout: 60000 }),
  assignStatementLandlord: (body) =>
    api.post('/admin/statements/landlord-assign', body, { timeout: 60000 }),
  // Statement property alias (unmapped refs + create alias)
  unmappedStatementProperties: () =>
    api.get('/admin/statements/unmapped-properties', { timeout: 60000 }),
  createStatementPropertyAlias: (body) =>
    api.post('/admin/statements/property-alias', body, { timeout: 60000 }),
  // Anchor-date coverage diagnostic (open-invoice classification)
  anchorDateCoverage: (params) =>
    api.get('/admin/diagnostics/anchor-date-coverage', { params, timeout: 120000 }),
  // BI Immo recon scope diagnostic (invoicing vs cash-receiving entity)
  biImmoReconScope: () =>
    api.get('/admin/diagnostics/bi-immo-recon-scope', { timeout: 120000 }),
  // BI Immo recon classification trust (cross-feed ambiguity audit)
  biImmoReconClassificationTrust: () =>
    api.get('/admin/diagnostics/bi-immo-recon-classification-trust', { timeout: 120000 }),
  // AFPV partial-apply diagnostic (read-only)
  afpvPartialApply: () =>
    api.get('/admin/diagnostics/afpv-partial-apply', { timeout: 120000 }),
  // AFPV partial-apply promote (re-apply through hardened path)
  afpvPartialApplyPromote: () =>
    api.post('/admin/diagnostics/afpv-partial-apply-promote', null, { timeout: 120000 }),
  // Revenue delta attribution (read-only)
  revenueDelta: (params) =>
    api.get('/admin/diagnostics/revenue-delta', { params, timeout: 120000 }),
  // Reconciliation audit (read-only, maps all surfaces)
  reconAudit: () =>
    api.get('/admin/diagnostics/recon-audit', { timeout: 120000 }),
  // Cross-feed duplicate candidates (read-only, informs dedup decision)
  crossFeedDuplicates: () =>
    api.get('/admin/diagnostics/cross-feed-duplicates', { timeout: 120000 }),
  // Contacts collision check (read-only, cross-credential live id overlap)
  contactsCollision: () =>
    api.get('/admin/diagnostics/contacts-collision', { timeout: 120000 }),
  // Contacts provenance backfill: stamps legacy unstamped docs matched to
  // exactly one active credential's live list. Preview is read-only.
  contactsCollisionBackfillPreview: () =>
    api.post('/admin/diagnostics/contacts-collision/backfill/preview', {}, { timeout: 120000 }),
  contactsCollisionBackfillApply: () =>
    api.post('/admin/diagnostics/contacts-collision/backfill/apply', {}, { timeout: 120000 }),
  // Reconciliation coverage (read-only, per entity per account + gap signal)
  reconciliationCoverage: () =>
    api.get('/admin/diagnostics/reconciliation-coverage', { timeout: 120000 }),
  // Per-booking reconciliation trace (read-only)
  reconTrace: (params) =>
    api.get('/admin/diagnostics/recon-trace', { params, timeout: 120000 }),
  // Reconciliation classifier (read-only, portfolio-wide)
  reconClassifier: () =>
    api.get('/admin/diagnostics/recon-classifier', { timeout: 120000 }),
  // Booking reconcile grouping diagnostic (read-only)
  bookingReconcileGrouping: (params) =>
    api.get('/admin/diagnostics/booking-reconcile-grouping', { params, timeout: 60000 }),
  // Commission classification diagnostic (read-only)
  commissionClassification: (params) =>
    api.get('/admin/diagnostics/commission-classification', { params, timeout: 60000 }),
  // AFPV auto-recon cron (last run + manual trigger)
  afpvAutoReconLastRun: () =>
    api.get('/admin/diagnostics/afpv-auto-recon-last-run', { timeout: 30000 }),
  afpvAutoReconRunNow: () =>
    api.post('/admin/diagnostics/afpv-auto-recon-run-now', null, { timeout: 120000 }),
  // Xero credential diagnostic (read-only, no secret values)
  xeroCredentials: () => api.get('/admin/diagnostics/xero-credentials'),
  // GoCardless status diagnostic (read-only): per-connection requisition
  // status, account/ingestion counts, rate-limit remaining, sync errors
  gocardlessStatus: () => api.get('/admin/diagnostics/gocardless-status'),
  // Xero single-invoice live lookup (report-only, all tenants)
  xeroInvoiceLookup: (number) =>
    api.get('/admin/diagnostics/xero-invoice-lookup', { params: { number }, timeout: 60000 }),
  // Group-wide inventory diagnostics (Prompt 1)
  entityStampDistribution: () =>
    api.get('/admin/diagnostics/entity-stamp-distribution', { timeout: 120000 }),
  costTypeDistribution: () =>
    api.get('/admin/diagnostics/cost-type-distribution', { timeout: 120000 }),
  bankAccountInventory: () =>
    api.get('/admin/diagnostics/bank-account-inventory', { timeout: 120000 }),
  brosBookingInvoiceLinkage: (params) =>
    api.get('/admin/diagnostics/bros-booking-invoice-linkage', { params, timeout: 120000 }),
  // Entity sync (Phase A)
  entityIdDerivationAudit: () =>
    api.get('/admin/diagnostics/entity-id-derivation-audit', { timeout: 120000 }),
  entitySyncPreview: () =>
    api.get('/admin/entities/sync-preview', { timeout: 30000 }),
  entitySyncApply: (body) =>
    api.post('/admin/entities/sync-apply', body, { timeout: 30000 }),
  // Bank account admin (Phase A)
  adminBankAccountInventory: () =>
    api.get('/admin/bank-accounts/inventory', { timeout: 30000 }),
  bankAccountBackfillPreview: () =>
    api.get('/admin/bank-accounts/backfill-preview', { timeout: 30000 }),
  bankAccountBackfillApply: () =>
    api.post('/admin/bank-accounts/backfill-apply', null, { timeout: 30000 }),
  bankAccountAssign: (accountId, body) =>
    api.patch(`/admin/bank-accounts/${accountId}/assign`, body),
  // Coverage matrix (Phase A)
  coverageMatrix: () =>
    api.get('/admin/entities/coverage-matrix', { timeout: 60000 }),
  // Entity merge (retire duplicate)
  entityMergePreview: (retireId = 'fz_pk', survivorId = 'fan_zhang_piotr_kacki') =>
    api.get('/admin/entity-merge/preview', { params: { retire_id: retireId, survivor_id: survivorId }, timeout: 60000 }),
  entityMergeApply: (retireId = 'fz_pk', survivorId = 'fan_zhang_piotr_kacki') =>
    api.post('/admin/entity-merge/apply', null, { params: { retire_id: retireId, survivor_id: survivorId }, timeout: 60000 }),
  // Entity account trace (diagnostic for bank-account mapping resolution)
  entityAccountTrace: (entityId) =>
    api.get('/admin/entity-account-trace', { params: { entity_id: entityId }, timeout: 60000 }),
  // Entity duplicates (normalized name collisions)
  entityDuplicatesScan: () =>
    api.get('/admin/entity-duplicates/scan', { timeout: 60000 }),
  entityDuplicatesMergePreview: (survivorUuid, retireUuid) =>
    api.get('/admin/entity-duplicates/merge-preview', {
      params: { survivor_uuid: survivorUuid, retire_uuid: retireUuid }, timeout: 60000,
    }),
  entityDuplicatesMergeApply: (survivorUuid, retireUuid) =>
    api.post('/admin/entity-duplicates/merge-apply', null, {
      params: { survivor_uuid: survivorUuid, retire_uuid: retireUuid }, timeout: 60000,
    }),
  // Entities feed integrity (duplicate ids / missing / orphan entities vs ENTITY_DEFS)
  entitiesFeedIntegrity: () =>
    api.get('/admin/diagnostics/entities-feed-integrity', { timeout: 60000 }),
  // "Remove identical twin": scoped to the byte-identical-duplicate-id case
  // only (routes/admin_entities_feed_integrity.py refuses anything else).
  entitiesFeedIntegrityDedupPreview: (entityId) =>
    api.get('/admin/diagnostics/entities-feed-integrity/dedup-preview', {
      params: { entity_id: entityId }, timeout: 30000,
    }),
  entitiesFeedIntegrityDedupApply: (entityId) =>
    api.post('/admin/diagnostics/entities-feed-integrity/dedup-apply', null, {
      params: { entity_id: entityId }, timeout: 30000,
    }),
  // Invoice currency diagnostic
  invoiceCurrencyDistribution: () =>
    api.get('/admin/diagnostics/invoice-currency-distribution', { timeout: 30000 }),
  invoiceCurrencyXeroCompare: (params) =>
    api.get('/admin/diagnostics/invoice-currency-xero-compare', { params, timeout: 120000 }),
  invoiceCurrencyVerdict: () =>
    api.get('/admin/diagnostics/invoice-currency-verdict', { timeout: 30000 }),
  // Invoice currency backfill (Commit 3)
  invoiceCurrencyBackfillPreview: () =>
    api.get('/admin/diagnostics/invoice-currency-backfill-preview', { timeout: 120000 }),
  invoiceCurrencyBackfillApply: () =>
    api.post('/admin/diagnostics/invoice-currency-backfill-apply', null, { timeout: 120000 }),
  // Retired-key unification backfill (see routes/admin_supplier_client_name_backfill.py)
  supplierClientNameBackfillPreview: () =>
    api.get('/admin/diagnostics/supplier-client-name-backfill/preview', { timeout: 60000 }),
  supplierClientNameBackfillApply: () =>
    api.post('/admin/diagnostics/supplier-client-name-backfill/apply', null, { timeout: 60000 }),
  // Pennylane counterparty (customer/supplier name) backfill (CLAUDE.md Prompt 8 Part 3)
  pennylaneCounterpartyBackfillPreview: () =>
    api.get('/admin/diagnostics/pennylane-counterparty-backfill/preview', { timeout: 300000 }),
  pennylaneCounterpartyBackfillApply: () =>
    api.post('/admin/diagnostics/pennylane-counterparty-backfill/apply', null, { timeout: 300000 }),
  pennylaneCounterpartyBackfillPopulationReconciliation: () =>
    api.get('/admin/diagnostics/pennylane-counterparty-backfill/population-reconciliation', { timeout: 300000 }),
  // Invoice currency reconciliation (storage-vs-Xero, aggregate-vs-converted)
  invoiceCurrencyReconciliation: (params) =>
    api.get('/admin/diagnostics/invoice-currency-reconciliation', { params, timeout: 120000 }),
  // Matching FX attribution: currency population vs SUPPORTED_CURRENCIES,
  // chain-run skip_reason_counts history, latest-run residuals
  matchingFxAttribution: (params) =>
    api.get('/admin/diagnostics/matching-fx-attribution', { params, timeout: 60000 }),
  // Bank connections inventory (read-only, three-section diagnostic)
  bankConnectionsInventory: () =>
    api.get('/admin/bank-connections/inventory', { timeout: 60000 }),
  // Security posture audit (read-only)
  securityPosture: () =>
    api.get('/admin/security/posture', { timeout: 120000 }),
  // Config readiness diagnostic (read-only, no secrets)
  configReadiness: () =>
    api.get('/admin/config/readiness'),
  // Token decryptability diagnostic (read-only, no secrets)
  tokenDecryptability: () =>
    api.get('/admin/diagnostics/token-decryptability', { timeout: 30000 }),
  // Google account connection health: stored scopes + a live one-call
  // Gmail API probe per account, so account_2-shaped mysteries
  // (gmail_api_not_configured etc) self-explain from one browser hit.
  googleConnectionHealth: () =>
    api.get('/admin/diagnostics/google-connection-health', { timeout: 30000 }),
  // Connect readiness pre-flight (read-only, no secrets)
  connectReadiness: () =>
    api.get('/admin/diagnostics/connect-readiness', { timeout: 30000 }),
  // Bridge Connect dry-run (credential + env check, read-only)
  bridgeConnectDryrun: () =>
    api.get('/admin/diagnostics/bridge-connect-dryrun', { timeout: 30000 }),
  // Bridge Connect chain dry-run (create_user -> auth -> connect_session, with cleanup)
  bridgeConnectChainDryrun: () =>
    api.post('/admin/diagnostics/bridge-connect-chain-dryrun', null, { timeout: 60000 }),
  // OB config consistency (env/URL agreement, read-only)
  obConfigConsistency: () =>
    api.get('/admin/diagnostics/ob-config-consistency', { timeout: 30000 }),
  // OB Connect dry-run (TrueLayer env + credential + provider check, read-only)
  obConnectDryrun: () =>
    api.get('/admin/diagnostics/ob-connect-dryrun', { timeout: 30000 }),
  // OB Connect chain dry-run (token -> accounts -> balance -> transactions, read-only)
  obConnectChainDryrun: () =>
    api.get('/admin/diagnostics/ob-connect-chain-dryrun', { timeout: 60000 }),
  // OB sync run-now (trigger one sync pass on demand)
  obSyncRunNow: () =>
    api.post('/admin/diagnostics/ob-sync-run-now', null, { timeout: 60000 }),
  // Revolut amount correction (100x scaling bug)
  revolutAmountCorrectionPreview: () =>
    api.get('/admin/revolut-amount-correction/preview', { timeout: 60000 }),
  revolutAmountCorrectionApply: () =>
    api.post('/admin/revolut-amount-correction/apply', null, { timeout: 120000 }),
  // Xero invoice ID gap count + live Xero match probe (read-only)
  xeroInvoiceIdGapCount: () =>
    api.get('/admin/diagnostics/xero-invoice-id-gap-count', { timeout: 180000 }),
  // Async gap scan (start-then-poll)
  xeroGapScanStart: () =>
    api.post('/admin/diagnostics/xero-invoice-id-gap-count/start'),
  // PDF provenance of not_in_xero invoices (read-only)
  invoicePdfProvenance: () =>
    api.get('/admin/diagnostics/invoice-pdf-provenance', { timeout: 180000 }),
  // Xero invoice ID backfill (bros_invoices): preview + apply
  xeroInvoiceIdBackfillPreview: () =>
    api.get('/admin/diagnostics/xero-invoice-id-backfill/preview', { timeout: 180000 }),
  xeroInvoiceIdBackfillApply: () =>
    api.post('/admin/diagnostics/xero-invoice-id-backfill/apply', null, { timeout: 180000 }),
  // Async backfill (start-then-poll)
  xeroBackfillPreviewStart: () =>
    api.post('/admin/diagnostics/xero-invoice-id-backfill/preview/start'),
  xeroBackfillApplyStart: () =>
    api.post('/admin/diagnostics/xero-invoice-id-backfill/apply/start'),
  // Job status polling + reload recovery
  xeroBackfillJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/xero-backfill-job-status/${jobId}`),
  xeroBackfillJobLatest: (operation) =>
    api.get(`/admin/diagnostics/xero-backfill-job-latest/${operation}`),
  xeroBackfillJobLatestSuccessful: (operation) =>
    api.get(`/admin/diagnostics/xero-backfill-job-latest-successful/${operation}`),
  // Pennylane entity_id backfill (db.invoices mis-stamped "bi_sas"): preview + apply
  pennylaneEntityBackfillPreviewStart: () =>
    api.post('/admin/diagnostics/pennylane-entity-backfill/preview/start'),
  pennylaneEntityBackfillApplyStart: () =>
    api.post('/admin/diagnostics/pennylane-entity-backfill/apply/start'),
  pennylaneEntityBackfillJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/pennylane-entity-backfill-job-status/${jobId}`),
  pennylaneEntityBackfillJobLatest: (operation) =>
    api.get(`/admin/diagnostics/pennylane-entity-backfill-job-latest/${operation}`),
  pennylaneEntityBackfillJobLatestSuccessful: (operation) =>
    api.get(`/admin/diagnostics/pennylane-entity-backfill-job-latest-successful/${operation}`),
  // Bank transaction entity_id backfill (account now assigned, tx stale): preview + apply
  bankTransactionEntityBackfillPreviewStart: () =>
    api.post('/admin/diagnostics/bank-transaction-entity-backfill/preview/start'),
  bankTransactionEntityBackfillApplyStart: () =>
    api.post('/admin/diagnostics/bank-transaction-entity-backfill/apply/start'),
  bankTransactionEntityBackfillJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/bank-transaction-entity-backfill-job-status/${jobId}`),
  bankTransactionEntityBackfillJobLatest: (operation) =>
    api.get(`/admin/diagnostics/bank-transaction-entity-backfill-job-latest/${operation}`),
  bankTransactionEntityBackfillJobLatestSuccessful: (operation) =>
    api.get(`/admin/diagnostics/bank-transaction-entity-backfill-job-latest-successful/${operation}`),
  // Pennylane invoice-file pull (PDF -> Drive): preview + apply
  pennylaneInvoiceFilesPreviewStart: (target = 'missing') =>
    api.post('/admin/pennylane-invoice-files/preview/start', { target }),
  pennylaneInvoiceFilesApplyStart: (target = 'missing') =>
    api.post('/admin/pennylane-invoice-files/apply/start', { target }),
  pennylaneInvoiceFilesJobStatus: (jobId) =>
    api.get(`/admin/pennylane-invoice-files/job-status/${jobId}`),
  pennylaneInvoiceFilesJobLatest: (operation) =>
    api.get(`/admin/pennylane-invoice-files/job-latest/${operation}`),
  pennylaneInvoiceFilesJobLatestSuccessful: (operation) =>
    api.get(`/admin/pennylane-invoice-files/job-latest-successful/${operation}`),
  // Read-only breakdown of check-failed rows (entity_id + resolved
  // credential company_id), grouped -- rides the same job-status/job-latest
  // endpoints as the pull above (operation="check_failed_breakdown").
  pennylaneCheckFailedBreakdownStart: (target = 'missing') =>
    api.post('/admin/pennylane-invoice-files/check-failed-breakdown/start', { target }),
  // Ownership probe: for each check-failed row, cross-checks every active
  // Pennylane credential's own API to find which one actually owns it.
  // Rides the same job-status/job-latest endpoints (operation="ownership_probe").
  pennylaneOwnershipProbeStart: (target = 'missing') =>
    api.post('/admin/pennylane-invoice-files/ownership-probe/start', { target }),
  // Reattribution: preview + apply, selected by the ownership probe's own
  // confirmation criteria (not a literal entity_id match). Rides the same
  // job-status/job-latest endpoints (operation="reattribution_preview" /
  // "reattribution_apply").
  pennylaneReattributionPreviewStart: (target = 'missing') =>
    api.post('/admin/pennylane-invoice-files/reattribution-preview/start', { target }),
  pennylaneReattributionApplyStart: (target = 'missing') =>
    api.post('/admin/pennylane-invoice-files/reattribution-apply/start', { target }),
  // Read-only: invoices a reattribution Apply tagged intercompany_candidate.
  // Not job-based -- a single fast Mongo query, no live Pennylane calls.
  pennylaneIntercompanyCandidates: () =>
    api.get('/admin/pennylane-invoice-files/intercompany-candidates'),
  // Pause control for the scheduled 6-hourly pull. Manual Preview/Apply
  // are never gated by this -- only the scheduled run checks it.
  pennylaneInvoiceFilesPauseStatus: () =>
    api.get('/admin/pennylane-invoice-files/pause-status'),
  pennylaneInvoiceFilesSetPause: (paused) =>
    api.put('/admin/pennylane-invoice-files/pause', { paused }),
  // CoS receipt Twilio-media rescue: preview + apply
  cosReceiptRescuePreviewStart: (target = 'missing') =>
    api.post('/admin/cos-receipt-rescue/preview/start', { target }),
  cosReceiptRescueApplyStart: (target = 'missing') =>
    api.post('/admin/cos-receipt-rescue/apply/start', { target }),
  cosReceiptRescueJobStatus: (jobId) =>
    api.get(`/admin/cos-receipt-rescue/job-status/${jobId}`),
  cosReceiptRescueJobLatest: (operation) =>
    api.get(`/admin/cos-receipt-rescue/job-latest/${operation}`),
  cosReceiptRescueJobLatestSuccessful: (operation) =>
    api.get(`/admin/cos-receipt-rescue/job-latest-successful/${operation}`),
  // Pause control for the scheduled 6-hourly rescue, same mechanism as
  // the Pennylane pull above.
  cosReceiptRescuePauseStatus: () =>
    api.get('/admin/cos-receipt-rescue/pause-status'),
  cosReceiptRescueSetPause: (paused) =>
    api.put('/admin/cos-receipt-rescue/pause', { paused }),
  // Misfiled-Drive-file audit: preview current-vs-canonical path + apply
  // relocation. No limit by default -- both walk the full candidate
  // population; passing one is an explicit override, not the routine path.
  drivePathAuditPreviewStart: (limit = null) =>
    api.post('/admin/drive-path-audit/preview/start', limit != null ? { limit } : {}),
  drivePathAuditApplyStart: (limit = null) =>
    api.post('/admin/drive-path-audit/apply/start', limit != null ? { limit } : {}),
  drivePathAuditJobStatus: (jobId) =>
    api.get(`/admin/drive-path-audit/job-status/${jobId}`),
  drivePathAuditJobLatest: (operation) =>
    api.get(`/admin/drive-path-audit/job-latest/${operation}`),
  drivePathAuditJobLatestSuccessful: (operation) =>
    api.get(`/admin/drive-path-audit/job-latest-successful/${operation}`),
  // Self-chunking Apply chain: each run time-boxes itself and schedules its
  // own successor from the persisted cursor until the population converges.
  // chain-status is the multi-run summary (status/runs_count); per-run live
  // progress still comes from the existing job-status/job-latest polling.
  drivePathAuditChainStatus: () =>
    api.get('/admin/drive-path-audit/apply/chain-status'),
  drivePathAuditChainStop: (chainId) =>
    api.post('/admin/drive-path-audit/apply/chain-stop', { chain_id: chainId }),
  // Read-only misfile explainer + duplicate-sibling census (prod-anomaly diagnostics)
  drivePathAuditExplainStart: (docIds = null, sampleSize = 25, scanLimit = 2000) =>
    api.post('/admin/drive-path-audit/explain/start', { doc_ids: docIds, sample_size: sampleSize, scan_limit: scanLimit }),
  drivePathAuditDuplicateCensusStart: (maxFolders = 3000) =>
    api.post('/admin/drive-path-audit/duplicate-census/start', { max_folders: maxFolders }),
  drivePathAuditRaceAudit: () =>
    api.get('/admin/drive-path-audit/race-audit'),
  drivePathAuditLocateTreeStart: (sampleSize = 10) =>
    api.post('/admin/drive-path-audit/locate-tree/start', { sample_size: sampleSize }),
  // Delete empty twin: the one action acting on the duplicate-sibling
  // census, targeting exactly one caller-specified folder_id. Synchronous
  // (no job/poll) -- a handful of Drive calls on one already-known folder.
  drivePathAuditDeleteEmptyTwinPreview: (folderId, parentId, name) =>
    api.post('/admin/drive-path-audit/delete-empty-twin/preview', { folder_id: folderId, parent_id: parentId, name }),
  drivePathAuditDeleteEmptyTwinApply: (folderId, parentId, name) =>
    api.post('/admin/drive-path-audit/delete-empty-twin/apply', { folder_id: folderId, parent_id: parentId, name }),
  // Invoice metadata quality census (CLAUDE.md Prompt 5): read-only,
  // counterparty/amount/ref population plus direction integrity (stored
  // type vs actual filename token vs actual folder segment). No limit by
  // default -- walks the full population; resumable via the same
  // cursor/seed convention as drivePathAuditPreviewStart if truncated.
  invoiceMetadataCensusStart: (limit = null) =>
    api.post('/admin/invoice-metadata-census/start', limit != null ? { limit } : {}),
  invoiceMetadataCensusJobStatus: (jobId) =>
    api.get(`/admin/invoice-metadata-census/job-status/${jobId}`),
  invoiceMetadataCensusJobLatest: () =>
    api.get('/admin/invoice-metadata-census/job-latest'),
  // Revolut counterparty historical backfill: live-refetches every
  // connected account's transactions (thousands of rows), so this rides
  // the job/poll infrastructure rather than a synchronous request.
  revolutCounterpartyBackfillPreviewStart: () =>
    api.post('/admin/diagnostics/revolut-counterparty-backfill/preview/start'),
  revolutCounterpartyBackfillApplyStart: () =>
    api.post('/admin/diagnostics/revolut-counterparty-backfill/apply/start'),
  revolutCounterpartyBackfillJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/revolut-counterparty-backfill/job-status/${jobId}`),
  revolutCounterpartyBackfillJobLatest: (operation) =>
    api.get(`/admin/diagnostics/revolut-counterparty-backfill/job-latest/${operation}`),
  revolutCounterpartyBackfillJobLatestSuccessful: (operation) =>
    api.get(`/admin/diagnostics/revolut-counterparty-backfill/job-latest-successful/${operation}`),
  // Enable Banking counterparty historical backfill: local re-parse (fast,
  // synchronous would suffice), but uses the same job/poll infrastructure
  // as the Revolut backfill above for UX consistency between the two.
  ebCounterpartyBackfillPreviewStart: () =>
    api.post('/admin/diagnostics/eb-counterparty-backfill/preview/start'),
  ebCounterpartyBackfillApplyStart: () =>
    api.post('/admin/diagnostics/eb-counterparty-backfill/apply/start'),
  ebCounterpartyBackfillJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/eb-counterparty-backfill/job-status/${jobId}`),
  ebCounterpartyBackfillJobLatest: (operation) =>
    api.get(`/admin/diagnostics/eb-counterparty-backfill/job-latest/${operation}`),
  ebCounterpartyBackfillJobLatestSuccessful: (operation) =>
    api.get(`/admin/diagnostics/eb-counterparty-backfill/job-latest-successful/${operation}`),
  // Enable Banking creditor-key census: what raw creditor/debtor keys and
  // credit_debit_indicator actually contain (read-only, synchronous)
  ebCreditorFieldCensus: () =>
    api.get('/admin/diagnostics/eb-counterparty-backfill/creditor-census', { timeout: 30000 }),
  // Google Drive destination root for invoice filing
  driveSettingsGet: () =>
    api.get('/admin/drive-settings'),
  driveSettingsUpdate: (rootFolderId, driveId) =>
    api.put('/admin/drive-settings', { root_folder_id: rootFolderId, drive_id: driveId || null }),
  // Xero invoice index (persisted per-tenant cache)
  xeroInvoiceIndexStatus: () =>
    api.get('/admin/diagnostics/xero-invoice-index/status'),
  xeroInvoiceIndexRebuild: (tenantId) =>
    api.post('/admin/diagnostics/xero-invoice-index/rebuild', null, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    }),
  // Xero Quota diagnostics (read-only, reads from call log)
  xeroQuota: () =>
    api.get('/admin/diagnostics/xero-quota', { timeout: 15000 }),
  xeroQuotaProbe: () =>
    api.post('/admin/diagnostics/xero-quota/probe', null, { timeout: 30000 }),
  // Bank accounts reader audit (hidden-filter compliance, read-only)
  bankAccountsReaderAudit: () =>
    api.get('/admin/diagnostics/bank-accounts-reader-audit', { timeout: 30000 }),
  // Total balance composition (per-account reconciliation against the Bank Balance stat card, read-only)
  totalBalanceComposition: () =>
    api.get('/admin/diagnostics/total-balance-composition', { timeout: 30000 }),
  // Bank transaction attribution (entity/counterparty fill-rate per source, read-only)
  bankTransactionAttribution: () =>
    api.get('/admin/diagnostics/bank-transaction-attribution', { timeout: 30000 }),
  // Sample up to sampleSize (default 10) recent transactions' raw payload from a
  // live connection (revolut/enablebanking) as a per-path fill-rate census
  bankTransactionAttributionSampleLivePayload: (source, sampleSize) =>
    api.get('/admin/diagnostics/bank-transaction-attribution/sample-live-payload', {
      params: { source, sample_size: sampleSize }, timeout: 30000,
    }),
  // Integration connection state (canonical predicate, read-only)
  integrationConnectionState: () =>
    api.get('/admin/diagnostics/integration-connection-state', { timeout: 30000 }),
  // Account transaction provenance: which feed(s) actually wrote an
  // account's recent transactions, runs against prod by definition.
  accountTransactionProvenance: (params) =>
    api.get('/admin/diagnostics/account-transaction-provenance', { params, timeout: 30000 }),
  // TrueLayer registry audit (provider_match resolution, read-only)
  truelayerRegistryAudit: () =>
    api.get('/admin/diagnostics/truelayer-registry-audit', { timeout: 30000 }),
  // Stale connection docs cleanup (preview-then-apply)
  staleConnectionsPreview: () =>
    api.get('/admin/diagnostics/stale-connections/preview', { timeout: 30000 }),
  staleConnectionsApply: () =>
    api.post('/admin/diagnostics/stale-connections/apply', null, { timeout: 30000 }),
  // Bridge bank resolution diagnostic (live provider matching, read-only)
  bridgeBankResolution: () =>
    api.get('/admin/diagnostics/bridge-bank-resolution', { timeout: 30000 }),
  // Revolut exchange config parity (structural fingerprints, read-only)
  revolutExchangeConfig: () =>
    api.get('/admin/diagnostics/revolut-exchange-config', { timeout: 30000 }),
  // Revolut exchange self-check (local assertion validity, read-only)
  revolutExchangeSelfCheck: (connectionId) =>
    api.get('/admin/diagnostics/revolut-exchange-self-check', {
      params: { connection_id: connectionId },
      timeout: 30000,
    }),
  // Entity duplicates (normalized-name collision scan + merge)
  entityDuplicatesScan: () =>
    api.get('/admin/entity-duplicates/scan', { timeout: 60000 }),
  entityDuplicatesMergePreview: (survivorUuid, retireUuid) =>
    api.get('/admin/entity-duplicates/merge-preview', {
      params: { survivor_uuid: survivorUuid, retire_uuid: retireUuid },
      timeout: 60000,
    }),
  entityDuplicatesMergeApply: (survivorUuid, retireUuid) =>
    api.post('/admin/entity-duplicates/merge-apply', null, {
      params: { survivor_uuid: survivorUuid, retire_uuid: retireUuid },
      timeout: 60000,
    }),
  // Runtime code fingerprint (live process introspection, read-only)
  runtimeFingerprint: () => api.get('/admin/runtime-fingerprint'),
  // Balance-sheet census (per-entity property/valuation/loan/receivable coverage)
  balanceSheetCensus: () => api.get('/admin/balance-sheet-census', { timeout: 120000 }),
  // Property valuations (manual entry)
  propertyValuations: (entityId) =>
    api.get('/admin/property-valuations', { params: { entity_id: entityId }, timeout: 30000 }),
  createPropertyValuation: (data) =>
    api.post('/admin/property-valuations', data),
  // Property loans (manual entry)
  propertyLoans: (entityId) =>
    api.get('/admin/property-loans', { params: { entity_id: entityId }, timeout: 30000 }),
  createPropertyLoan: (data) =>
    api.post('/admin/property-loans', data),
  // Receivables overlap diagnostic (booking/invoice dedup analysis)
  receivablesOverlap: () => api.get('/admin/diagnostics/receivables-overlap', { timeout: 120000 }),
  // P&L reconciliation diagnostic (entity_pnl consistency checks)
  pnlReconciliation: (params) => api.get('/admin/diagnostics/pnl-reconciliation', { params, timeout: 120000 }),
  // Group Dashboard vs Property Portfolio revenue parity diagnostic (per-property diff)
  portfolioRevenueParity: (params) =>
    api.get('/admin/diagnostics/portfolio-revenue-parity', { params, timeout: 120000 }),
  // Receivables breakdown diagnostic (A&L Current Assets receivables decomposition)
  receivablesBreakdown: (entityId, displayCurrency = 'GBP') =>
    api.get('/admin/diagnostics/receivables-breakdown', {
      params: { entity_id: entityId, display_currency: displayCurrency },
      timeout: 120000,
    }),
  // A&L invoice drilldown (per-invoice listing behind "BR OS client
  // invoices" / "Unpaid invoices", with possible_bros_twin heuristic)
  alInvoiceDrilldown: (entityId) =>
    api.get('/admin/diagnostics/al-invoice-drilldown', {
      params: { entity_id: entityId },
      timeout: 60000,
    }),
  // A&L managed-booking routing matrix (property x booking management_type
  // x payment_state cross-tab, with the not-stamped-direct total)
  alManagedBookingMatrix: (entityId) =>
    api.get('/admin/diagnostics/al-managed-booking-matrix', {
      params: { entity_id: entityId },
      timeout: 60000,
    }),
  // br_ltd Xero receivable provenance (TRACE/SERIES/NEITHER/NON_XERO_SOURCE
  // split of the al-invoice-drilldown receivable population, plus the PHD
  // Stillads ingestion-state read)
  alInvoiceProvenance: (entityId) =>
    api.get('/admin/diagnostics/al-invoice-provenance', {
      params: { entity_id: entityId },
      timeout: 60000,
    }),
  // First-activation Preview/Apply gate: excludes TRACE-linked Xero twins
  // from the br_ltd receivable and routes them into eliminated overlap at
  // their authoritative bros_invoices amount (routes/admin_xero_trace_dedup.py).
  previewXeroTraceDedup: () =>
    api.post('/admin/diagnostics/xero-trace-dedup', { dry_run: true }),
  applyXeroTraceDedup: () =>
    api.post('/admin/diagnostics/xero-trace-dedup', { dry_run: false }),
  // br_ltd receivables second-axis dedup census: buckets Xero invoices that
  // survive the existing BR OS elimination into (a) carries linkage, (b) no
  // linkage found (routes/admin_br_ltd_invoice_linkage.py). Read-only. No
  // heuristic matching is proposed.
  brLtdInvoiceLinkageCensus: () =>
    api.get('/admin/diagnostics/br-ltd-invoice-linkage-census', { timeout: 60000 }),
  // Takes ONLY an operator-hand-entered (invoice_id, bros_id) pair and
  // stamps xero_invoice_id/xero_tenant_id onto the bros_invoices side.
  // Never infers, suggests, or auto-applies a pairing.
  applyBrLtdInvoiceLinkageRepair: (pairs) =>
    api.post('/admin/diagnostics/br-ltd-invoice-linkage-repair', { pairs }),
  // Xero tenant resolution: live connected-tenant mapping + historical
  // mis-attribution audit (routes/admin_xero_tenant_resolution.py). Read-only.
  xeroTenantResolution: () =>
    api.get('/admin/diagnostics/xero-tenant-resolution'),
  // Intercompany position diagnostic (pair entries + candidate settlements)
  intercompanyPosition: (params) =>
    api.get('/admin/diagnostics/intercompany-position', { params, timeout: 120000 }),
  // Opex source diagnostic (canonical entity_opex vs recurring-cost coverage)
  opexSource: (entityId, year) =>
    api.get('/admin/diagnostics/opex-source', {
      params: { entity_id: entityId, year },
      timeout: 120000,
    }),
  // Gmail amount 0/missing cohort re-extraction (CLAUDE.md Prompt 10):
  // budget-boxed chunked chains (same engine as drivePathAudit* above) --
  // each run time-boxes itself and schedules its own successor from the
  // persisted cursor/tallies until the cohort converges, instead of one
  // synchronous call re-running an LLM extraction + Drive fetch per cohort
  // doc inline (the old shape 504'd at scale).
  gmailAmountReextractionPreviewChainStart: () =>
    api.post('/admin/diagnostics/gmail-amount-reextraction/preview/chain-start'),
  gmailAmountReextractionPreviewChainStatus: () =>
    api.get('/admin/diagnostics/gmail-amount-reextraction/preview/chain-status'),
  gmailAmountReextractionPreviewChainStop: (chainId) =>
    api.post('/admin/diagnostics/gmail-amount-reextraction/preview/chain-stop', { chain_id: chainId }),
  gmailAmountReextractionApplyChainStart: () =>
    api.post('/admin/diagnostics/gmail-amount-reextraction/apply/chain-start'),
  gmailAmountReextractionApplyChainStatus: () =>
    api.get('/admin/diagnostics/gmail-amount-reextraction/apply/chain-status'),
  gmailAmountReextractionApplyChainStop: (chainId) =>
    api.post('/admin/diagnostics/gmail-amount-reextraction/apply/chain-stop', { chain_id: chainId }),
  gmailAmountReextractionJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/gmail-amount-reextraction/job-status/${jobId}`),
  gmailAmountReextractionJobLatest: (operation) =>
    api.get(`/admin/diagnostics/gmail-amount-reextraction/job-latest/${operation}`),
  // Gmail receipt-body re-parse: deterministic (no-LLM) regex re-parse of
  // the amount_parse_suspect / quarantine_field=="amount" population's own
  // stored email_body against the standard Stripe receipt template. Same
  // budget-boxed chunked-chain engine as gmailAmountReextraction* above.
  gmailReceiptReparsePreviewChainStart: () =>
    api.post('/admin/diagnostics/gmail-receipt-reparse/preview/chain-start'),
  gmailReceiptReparsePreviewChainStatus: () =>
    api.get('/admin/diagnostics/gmail-receipt-reparse/preview/chain-status'),
  gmailReceiptReparsePreviewChainStop: (chainId) =>
    api.post('/admin/diagnostics/gmail-receipt-reparse/preview/chain-stop', { chain_id: chainId }),
  gmailReceiptReparseApplyChainStart: () =>
    api.post('/admin/diagnostics/gmail-receipt-reparse/apply/chain-start'),
  gmailReceiptReparseApplyChainStatus: () =>
    api.get('/admin/diagnostics/gmail-receipt-reparse/apply/chain-status'),
  gmailReceiptReparseApplyChainStop: (chainId) =>
    api.post('/admin/diagnostics/gmail-receipt-reparse/apply/chain-stop', { chain_id: chainId }),
  gmailReceiptReparseJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/gmail-receipt-reparse/job-status/${jobId}`),
  gmailReceiptReparseJobLatest: (operation) =>
    api.get(`/admin/diagnostics/gmail-receipt-reparse/job-latest/${operation}`),
  // Outbound correspondence (2026-08-25 prompt "zero-amount invoices"): the
  // document_type=="invoice" zero-amount cohort's own-domain-sender,
  // no-PDF subset -- the operator's own outbound correspondence, not a
  // real invoice (see services/outbound_correspondence.py). Census is
  // read-only; Preview/Apply is the same budget-boxed chunked-chain engine
  // as gmailReceiptReparse* above.
  outboundCorrespondenceCensus: () =>
    api.get('/admin/diagnostics/gmail-receipt-reparse/outbound-correspondence-census'),
  outboundCorrespondencePreviewChainStart: () =>
    api.post('/admin/diagnostics/gmail-receipt-reparse/outbound-correspondence/preview/chain-start'),
  outboundCorrespondencePreviewChainStatus: () =>
    api.get('/admin/diagnostics/gmail-receipt-reparse/outbound-correspondence/preview/chain-status'),
  outboundCorrespondencePreviewChainStop: (chainId) =>
    api.post('/admin/diagnostics/gmail-receipt-reparse/outbound-correspondence/preview/chain-stop', { chain_id: chainId }),
  outboundCorrespondenceApplyChainStart: () =>
    api.post('/admin/diagnostics/gmail-receipt-reparse/outbound-correspondence/apply/chain-start'),
  outboundCorrespondenceApplyChainStatus: () =>
    api.get('/admin/diagnostics/gmail-receipt-reparse/outbound-correspondence/apply/chain-status'),
  outboundCorrespondenceApplyChainStop: (chainId) =>
    api.post('/admin/diagnostics/gmail-receipt-reparse/outbound-correspondence/apply/chain-stop', { chain_id: chainId }),
  outboundCorrespondenceJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/gmail-receipt-reparse/outbound-correspondence/job-status/${jobId}`),
  outboundCorrespondenceJobLatest: (operation) =>
    api.get(`/admin/diagnostics/gmail-receipt-reparse/outbound-correspondence/job-latest/${operation}`),
  // Rule-1 backfill: receipt-template docs already ingested with
  // payment_status != "paid" before 4237288a's ingestion-time fix landed.
  // Same budget-boxed chunked-chain engine as gmailReceiptReparse* above,
  // touches payment_status only.
  gmailReceiptPaidStatusBackfillPreviewChainStart: () =>
    api.post('/admin/diagnostics/gmail-receipt-paid-status-backfill/preview/chain-start'),
  gmailReceiptPaidStatusBackfillPreviewChainStatus: () =>
    api.get('/admin/diagnostics/gmail-receipt-paid-status-backfill/preview/chain-status'),
  gmailReceiptPaidStatusBackfillPreviewChainStop: (chainId) =>
    api.post('/admin/diagnostics/gmail-receipt-paid-status-backfill/preview/chain-stop', { chain_id: chainId }),
  gmailReceiptPaidStatusBackfillApplyChainStart: () =>
    api.post('/admin/diagnostics/gmail-receipt-paid-status-backfill/apply/chain-start'),
  gmailReceiptPaidStatusBackfillApplyChainStatus: () =>
    api.get('/admin/diagnostics/gmail-receipt-paid-status-backfill/apply/chain-status'),
  gmailReceiptPaidStatusBackfillApplyChainStop: (chainId) =>
    api.post('/admin/diagnostics/gmail-receipt-paid-status-backfill/apply/chain-stop', { chain_id: chainId }),
  gmailReceiptPaidStatusBackfillJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/gmail-receipt-paid-status-backfill/job-status/${jobId}`),
  gmailReceiptPaidStatusBackfillJobLatest: (operation) =>
    api.get(`/admin/diagnostics/gmail-receipt-paid-status-backfill/job-latest/${operation}`),
  // Amount backfill: receipt-template docs stuck at amount 0/null/missing
  // (includes the docs the Rule-1 backfill above already flipped to paid).
  // Writes amount + currency-from-symbol on an "Amount paid" body match,
  // plus the Rule-1 payment_status flip in the same write when the doc
  // isn't already paid. Same budget-boxed chunked-chain engine as
  // gmailReceiptPaidStatusBackfill* above.
  gmailReceiptAmountBackfillPreviewChainStart: () =>
    api.post('/admin/diagnostics/gmail-receipt-amount-backfill/preview/chain-start'),
  gmailReceiptAmountBackfillPreviewChainStatus: () =>
    api.get('/admin/diagnostics/gmail-receipt-amount-backfill/preview/chain-status'),
  gmailReceiptAmountBackfillPreviewChainStop: (chainId) =>
    api.post('/admin/diagnostics/gmail-receipt-amount-backfill/preview/chain-stop', { chain_id: chainId }),
  gmailReceiptAmountBackfillApplyChainStart: () =>
    api.post('/admin/diagnostics/gmail-receipt-amount-backfill/apply/chain-start'),
  gmailReceiptAmountBackfillApplyChainStatus: () =>
    api.get('/admin/diagnostics/gmail-receipt-amount-backfill/apply/chain-status'),
  gmailReceiptAmountBackfillApplyChainStop: (chainId) =>
    api.post('/admin/diagnostics/gmail-receipt-amount-backfill/apply/chain-stop', { chain_id: chainId }),
  gmailReceiptAmountBackfillJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/gmail-receipt-amount-backfill/job-status/${jobId}`),
  gmailReceiptAmountBackfillJobLatest: (operation) =>
    api.get(`/admin/diagnostics/gmail-receipt-amount-backfill/job-latest/${operation}`),
  // Read-only per-sender residue samples (total_only / no_signal docs the
  // amount backfill above could never resolve) -- for pulling real body
  // evidence to write new per-sender parser templates from.
  gmailReceiptAmountBackfillResidueSamples: (senderContains) =>
    api.get('/admin/diagnostics/gmail-receipt-amount-backfill/residue-samples', {
      params: senderContains ? { sender_contains: senderContains } : {},
    }),
  // Non-Invoice Cleanup: flags Gmail junk (bounces/calendar invites/
  // newsletters) that slipped through pre-gate ingestion. Budget-boxed
  // chunked chains (same engine as gmailAmountReextraction* above) -- the
  // cohort now also covers pending_review (not just quarantined), which can
  // be materially larger than this was originally sized for.
  nonInvoiceCleanupPreviewChainStart: () =>
    api.post('/admin/diagnostics/non-invoice-cleanup/preview/chain-start'),
  nonInvoiceCleanupPreviewChainStatus: () =>
    api.get('/admin/diagnostics/non-invoice-cleanup/preview/chain-status'),
  nonInvoiceCleanupPreviewChainStop: (chainId) =>
    api.post('/admin/diagnostics/non-invoice-cleanup/preview/chain-stop', { chain_id: chainId }),
  nonInvoiceCleanupApplyChainStart: () =>
    api.post('/admin/diagnostics/non-invoice-cleanup/apply/chain-start'),
  nonInvoiceCleanupApplyChainStatus: () =>
    api.get('/admin/diagnostics/non-invoice-cleanup/apply/chain-status'),
  nonInvoiceCleanupApplyChainStop: (chainId) =>
    api.post('/admin/diagnostics/non-invoice-cleanup/apply/chain-stop', { chain_id: chainId }),
  nonInvoiceCleanupJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/non-invoice-cleanup/job-status/${jobId}`),
  nonInvoiceCleanupJobLatest: (operation) =>
    api.get(`/admin/diagnostics/non-invoice-cleanup/job-latest/${operation}`),
  // Read-only: confirms whether the deterministic gate's has_pdf exemption
  // (any PDF attachment skips the no-pdf-no-amount check) is why known-junk
  // PDF-carrying docs (a CV, a marketing brochure) survive the cleanup
  // cohort uncaught. No writes, no classifier change.
  nonInvoiceCleanupPdfBypassNearMiss: () =>
    api.get('/admin/diagnostics/non-invoice-cleanup/pdf-bypass-near-miss'),
  // Broadened-cohort gate (Commit 2): entity resolution doesn't rescue a
  // junk-shaped doc, so Apply is eligible to exclude a resolved-entity or
  // rejected-status doc too -- but only once an operator confirms having
  // reviewed a Preview run's would-exclude list for that newly-in-scope
  // subset. Defaults to unconfirmed.
  nonInvoiceCleanupBroadenedCohortGateStatus: () =>
    api.get('/admin/diagnostics/non-invoice-cleanup/broadened-cohort-gate'),
  nonInvoiceCleanupBroadenedCohortGateConfirm: () =>
    api.post('/admin/diagnostics/non-invoice-cleanup/broadened-cohort-gate/confirm'),
  // Type-classify suspects (Fan's document-type classification, Commit 2):
  // retroactive pass over the standing amount_parse_suspect/pending_review-
  // no-amount population, reusing services/document_classification.py's
  // SAME disposition rule the live-ingest path uses. Preview never writes;
  // Apply is gated behind a completed Preview over the same cohort.
  documentTypeClassificationPreviewChainStart: () =>
    api.post('/admin/diagnostics/document-type-classification/preview/chain-start'),
  documentTypeClassificationPreviewChainStatus: () =>
    api.get('/admin/diagnostics/document-type-classification/preview/chain-status'),
  documentTypeClassificationPreviewChainStop: (chainId) =>
    api.post('/admin/diagnostics/document-type-classification/preview/chain-stop', { chain_id: chainId }),
  documentTypeClassificationApplyChainStart: () =>
    api.post('/admin/diagnostics/document-type-classification/apply/chain-start'),
  documentTypeClassificationApplyChainStatus: () =>
    api.get('/admin/diagnostics/document-type-classification/apply/chain-status'),
  documentTypeClassificationApplyChainStop: (chainId) =>
    api.post('/admin/diagnostics/document-type-classification/apply/chain-stop', { chain_id: chainId }),
  documentTypeClassificationJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/document-type-classification/job-status/${jobId}`),
  documentTypeClassificationJobLatest: (operation) =>
    api.get(`/admin/diagnostics/document-type-classification/job-latest/${operation}`),
  // Invoice Row Explain: per-row diagnostic answering why one Supplier
  // Invoices row looks the way it does and what the Non-Invoice Cleanup
  // sweep has done or will do with it. Read-only.
  invoiceRowExplainCandidates: () =>
    api.get('/admin/diagnostics/invoice-row-explain/candidates'),
  invoiceRowExplain: (invoiceId) =>
    api.get(`/admin/diagnostics/invoice-row-explain/${encodeURIComponent(invoiceId)}`),
  // Zero-Amount Invoice Census: sizes the "amount==0 and
  // amount_parse_suspect==false" population (a historical pre-fix cohort
  // -- see services/invoice_zero_amount_census.py) plus a heuristic
  // currency-default census and a receipt/invoice sibling-pair check.
  // Read-only.
  invoiceZeroAmountCensus: () =>
    api.get('/admin/diagnostics/invoice-zero-amount-census'),
  invoiceCurrencyDefaultCensus: () =>
    api.get('/admin/diagnostics/invoice-currency-default-census'),
  // Parsed-pair duplicate census (Commit 3/3, the Aircall gap): two
  // gmail-sourced invoices that BOTH parsed successfully to the same
  // amount -- invisible to the zero-amount census above. Read-only.
  invoiceParsedPairDuplicateCensus: () =>
    api.get('/admin/diagnostics/invoice-parsed-pair-duplicate-census'),
  // Approved-zero source verification: live-checks each approved,
  // accounting-sourced zero-amount doc against Pennylane/Xero itself.
  // GET reads the persisted verdicts (no live calls); POST .../run
  // re-verifies live and refreshes them. Read-only w.r.t. invoice docs.
  invoiceZeroAmountSourceVerification: () =>
    api.get('/admin/diagnostics/invoice-zero-amount-source-verification'),
  invoiceZeroAmountSourceVerificationRun: () =>
    api.post('/admin/diagnostics/invoice-zero-amount-source-verification/run'),
  // Zero-amount invoice closeout (2026-08-25 prompt): duplicate merge runs
  // first (a zero doc with a real-amount sibling merges into it, never
  // becomes a payable of its own), then the terminal "nothing due"
  // classification (source-verified-zero docs with no sibling). Preview
  // never writes; /apply does. Gated on the persisted source-verification
  // verdict above -- never the heuristic currency-default proxy.
  invoiceZeroAmountDuplicateMerge: () =>
    api.get('/admin/diagnostics/invoice-zero-amount-duplicate-merge'),
  invoiceZeroAmountDuplicateMergeApply: () =>
    api.post('/admin/diagnostics/invoice-zero-amount-duplicate-merge/apply'),
  invoiceZeroAmountTerminal: () =>
    api.get('/admin/diagnostics/invoice-zero-amount-terminal'),
  invoiceZeroAmountTerminalApply: () =>
    api.post('/admin/diagnostics/invoice-zero-amount-terminal/apply'),
  invoiceZeroAmountCloseoutSummary: () =>
    api.get('/admin/diagnostics/invoice-zero-amount-closeout-summary'),
  // Per-doc explain table: for every doc in the census cohort, which
  // merge/terminal bucket it landed in and why. Permanent "census says X,
  // preview says Y" answer for the population-divergence bug class.
  invoiceZeroAmountDisposition: () =>
    api.get('/admin/diagnostics/invoice-zero-amount-disposition'),
  // Rejected Invoice Exposure (exclusion-audit slice 1, commit 2): read-only
  // restatement-sizing deltas per money-wrong surface, plus a damage census
  // for the live-push path commit 1 closed (rejected invoices already
  // matched or already pushed to Pennylane). Never writes anything.
  rejectedInvoiceExposure: (year) =>
    api.get('/admin/diagnostics/rejected-invoice-exposure', { params: year ? { year } : {} }),
  // Client Invoice Census: read-only breakdown of the Client Invoices
  // population by source/review_status/taxonomy/matching_status. GET only,
  // no chain -- the aggregation runs server-side in Mongo and completes
  // within one request regardless of collection size.
  clientInvoiceCensus: () =>
    api.get('/admin/diagnostics/client-invoice-census'),
  // Supplier Invoice Census: mirror of clientInvoiceCensus for the
  // payable/inbound side. Both responses also carry a
  // direction_reconciliation block (total/payable/receivable/
  // unclassifiable two-way check).
  supplierInvoiceCensus: () =>
    api.get('/admin/diagnostics/supplier-invoice-census'),
  // Cross-source duplication census: read-only, pairwise (normalized
  // invoice_number, amount, entity_id) overlap counts across every source
  // pair present in db.invoices (e.g. bros x xero). Report only, no writes.
  invoiceDuplicateCensus: () =>
    api.get('/admin/diagnostics/invoice-duplicate-census'),
  // Pennylane recon propagation trace: for one invoice, the local match
  // doc, its own accounting-sync fields, the stored (pulled) Pennylane
  // reconciliation record(s), and any push-attempt sync_logs entries.
  // Read-only, no live Pennylane calls (2026-07-27 AFPV Bank
  // Transactions "stuck Pending" investigation).
  pennylaneReconPropagation: (invoice) =>
    api.get('/admin/diagnostics/pennylane-recon-propagation', { params: { invoice } }),
  // Live stale-queued check (2026-08-11 stuck-queue fix): computed on
  // demand, not read from a stored watchdog log entry -- a stalled retry
  // job can't be trusted to have logged its own staleness recently.
  pennylaneQueueHealth: () =>
    api.get('/admin/diagnostics/pennylane-recon-propagation/queue-health'),
  // "Queue push now": Preview (GET, read-only) shows entity/credential
  // source/predicted target push_state; Apply (POST) sets push_state=
  // "queued" -- it does not call Pennylane itself, only re-enters the
  // durable retry queue (services/pennylane_push_retry.py).
  pennylaneQueuePushPreview: (invoiceId) =>
    api.get('/admin/diagnostics/pennylane-recon-propagation/queue-push/preview', { params: { invoice_id: invoiceId } }),
  pennylaneQueuePushApply: (invoiceId) =>
    api.post('/admin/diagnostics/pennylane-recon-propagation/queue-push/apply', { invoice_id: invoiceId }),
  // "Queue stuck invoices" bulk backfill (2026-07-29): Preview (GET,
  // read-only) shows the whole backlog of Pennylane invoices reconciled/
  // paid locally but never (re-)queued, with per-entity/per-month counts
  // and a drain-runs estimate; Apply (POST) queues exactly that population,
  // computed fresh server-side -- idempotent, safe to re-run.
  pennylaneQueueStuckPreview: () =>
    api.get('/admin/diagnostics/pennylane-recon-propagation/queue-stuck/preview'),
  pennylaneQueueStuckApply: () =>
    api.post('/admin/diagnostics/pennylane-recon-propagation/queue-stuck/apply'),
  // AFPV invoice combo-match census: report-only, tests whether a given
  // (tx_amount, date) could be a batched payout covering 2-4 open AFPV
  // invoices within a date window. Never links or writes anything.
  afpvInvoiceComboMatch: (txAmount, date) =>
    api.get('/admin/diagnostics/afpv-invoice-combo-match', { params: { tx_amount: txAmount, date } }),
  // "Push Reconciliations to Pennylane": Preview (GET, read-only, no live
  // Pennylane calls) lists every invoice services/pennylane_push_retry.py's
  // run_pennylane_push_retry would act on right now, with a predicted
  // eligibility (would_push / would_skip:<reason> / cross_entity_blocked /
  // possible_conflict). Apply (POST) triggers that same function on demand,
  // out of band from its normal 6h schedule -- every write still goes
  // through its own guards (cross-entity block, conflict guard, credential
  // resolution). Idempotent -- re-Apply only acts on what's still eligible.
  pennylanePushDiagnosticsPreview: () =>
    api.get('/admin/diagnostics/pennylane-push-diagnostics/preview'),
  pennylanePushDiagnosticsApply: () =>
    api.post('/admin/diagnostics/pennylane-push-diagnostics/apply'),
  // Reconciliation Disagreements: read-only, no writes. Two row kinds --
  // counterpart_mismatch (ledger says reconciled to invoice X, FA settled
  // the same transaction against invoice Y) and push_conflict (Direction 1's
  // conflict guard, services/pennylane_push_retry.py, refused to overwrite
  // an existing different-counterpart Pennylane link).
  reconciliationDisagreements: () =>
    api.get('/admin/diagnostics/reconciliation-disagreements'),
  // UK Booking Census (FA vs PMS): FA UK bookings for a month (same basis
  // as the Bookings-tab month bucket) joined against a live Hostaway fetch.
  // Read-only. Live PMS call can be slow (full paginated reservation walk).
  ukBookingCensus: (month) =>
    api.get('/admin/diagnostics/uk-booking-census', { params: month ? { month } : {}, timeout: 180000 }),

  // UK Hostaway Listings recognition + windowed reservation backfill front
  // door (see routes/admin_uk_booking_census.py). Read-only join for the
  // listing table; writes for owned/map-row edits go through hostawayAPI
  // (updatePropertyOwned / saveListingMap) -- this module never duplicates
  // those writers.
  ukHostawayListings: () => api.get('/admin/uk-hostaway-listings'),
  ukHostawayBackfillPreview: (body) =>
    api.post('/admin/uk-hostaway-backfill/preview', body, { timeout: 120000 }),
  ukHostawayBackfillApply: (body) =>
    api.post('/admin/uk-hostaway-backfill/apply', body, { timeout: 180000 }),

  // Bank Fee / Cashback document-free reconciliation (Fan, 2026-08-25):
  // read-only Phase-0 marker census, plus Preview/Apply chain over the
  // self-clearing category-in-(bank_fee,cashback)-and-unreconciled cohort.
  // Same chain shape as documentTypeClassification* above.
  bankFeeCashbackCensus: () =>
    api.get('/admin/diagnostics/bank-fee-cashback/census'),
  bankFeeCashbackPreviewChainStart: () =>
    api.post('/admin/diagnostics/bank-fee-cashback/preview/chain-start'),
  bankFeeCashbackPreviewChainStatus: () =>
    api.get('/admin/diagnostics/bank-fee-cashback/preview/chain-status'),
  bankFeeCashbackPreviewChainStop: (chainId) =>
    api.post('/admin/diagnostics/bank-fee-cashback/preview/chain-stop', { chain_id: chainId }),
  bankFeeCashbackApplyChainStart: () =>
    api.post('/admin/diagnostics/bank-fee-cashback/apply/chain-start'),
  bankFeeCashbackApplyChainStatus: () =>
    api.get('/admin/diagnostics/bank-fee-cashback/apply/chain-status'),
  bankFeeCashbackApplyChainStop: (chainId) =>
    api.post('/admin/diagnostics/bank-fee-cashback/apply/chain-stop', { chain_id: chainId }),
  bankFeeCashbackJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/bank-fee-cashback/job-status/${jobId}`),
  bankFeeCashbackJobLatest: (operation) =>
    api.get(`/admin/diagnostics/bank-fee-cashback/job-latest/${operation}`),

  // Wise Cashback historical recovery (Fan, 2026-08-25): Wise's
  // CARD_CASHBACK activity type is captured at ingestion now, but a row
  // synced before that capture existed is ambiguous from stored data alone
  // (payment_method collapses CARD_PAYMENT/CARD_REFUND/CARD_CASHBACK onto
  // one value) -- this walks Wise's own activities API in bounded
  // increments to resolve them. Stamps category=cashback only; re-run
  // bankFeeCashback* Apply afterwards to reconcile.
  wiseCashbackCensus: () =>
    api.get('/admin/diagnostics/wise-cashback/census'),
  wiseCashbackPreviewChainStart: () =>
    api.post('/admin/diagnostics/wise-cashback/preview/chain-start'),
  wiseCashbackPreviewChainStatus: () =>
    api.get('/admin/diagnostics/wise-cashback/preview/chain-status'),
  wiseCashbackPreviewChainStop: (chainId) =>
    api.post('/admin/diagnostics/wise-cashback/preview/chain-stop', { chain_id: chainId }),
  wiseCashbackApplyChainStart: () =>
    api.post('/admin/diagnostics/wise-cashback/apply/chain-start'),
  wiseCashbackApplyChainStatus: () =>
    api.get('/admin/diagnostics/wise-cashback/apply/chain-status'),
  wiseCashbackApplyChainStop: (chainId) =>
    api.post('/admin/diagnostics/wise-cashback/apply/chain-stop', { chain_id: chainId }),
  wiseCashbackJobStatus: (jobId) =>
    api.get(`/admin/diagnostics/wise-cashback/job-status/${jobId}`),
  wiseCashbackJobLatest: (operation) =>
    api.get(`/admin/diagnostics/wise-cashback/job-latest/${operation}`),
};

// Sweep Blast Radius: read-only census of everything superseded by the
// Stale Suggested Matches Sweep (matchingAPI.applyStaleSuggestedMatchesSweep
// above) -- census by kind/reason, a structural 0-check that a confirmed
// match can never carry the sweep marker, prior-confirm-then-reverted
// residue detection, reconciliation_memory orphans, and a cross-collection
// contamination check against statement_payout_matches / recurring_cost_matches.
// GET only, never writes.
export const sweepBlastRadiusAPI = {
  run: () => api.get('/admin/sweep-blast-radius', { timeout: 30000 }),
};

// Entity Field Corruption Census: read-only, two sections in one call --
// (1) db.transactions/db.invoices docs where entity_id exists but is not a
// string (raw ObjectId etc, CLAUDE.md section 5), (2) every collection's
// docs whose _id starts with repro/seed/test (leftover fixture residue).
// GET only, never writes anywhere.
export const entityFieldCorruptionCensusAPI = {
  run: () => api.get('/admin/diagnostics/entity-field-corruption-census', { timeout: 30000 }),
};
